import {createSelector} from 'reselect';
import {get} from 'lodash';

import {
    getAbacusTests,
    getSystem,
    getTranslations,
    getTravelerFeatures,
    getTripData
} from './store';
import {getFirstPayment} from './priceDetails';
import getRateDetailsData from './rateDetails';
import {getListingPageModalStates} from './modalStates';
import getAvailabilityCalendar from './availabilityCalendar';
import getHouseRulesData from './houseRules';
import {getRecommendedProperties} from './recommendedProperties';
import getBookingUrl from './bookingUrl';
import getBookingModalData from './bookingModal';
import {getJsReviews, getBearStayBannerLabels} from './reviews';
import {getListingSummary} from './listingSummary';
import {getIsTypeaheadInputFocused} from './listingPageState';
import {getViewDetailsTotalData} from './rightRail';
import {getMapData} from './map';
import {getDates, getGuests} from './searchBox';
import {getBaseUrlPath, getBrandName, getBrowserName, getSiteName} from './system';
import {getTravelerSegmentFeatures} from './travelerSegmentFeatures';
import {
    getAccomodationSummary,
    getBearStayReviewData,
    getBookingType,
    getDescription,
    getHostType,
    getPropertyName,
    getSearchLandingPages,
    getListingIsFamilyFriendly,
    getVirtualTourData
} from './listing';
import removeUniId from '../utils/recommendedLinksUtils';
import {getMainImageHeight} from './imageCarousel';

const getListingDescription = createSelector(
    [getAccomodationSummary, getDescription, getPropertyName],
    (accomodationSummary, description, propertyName) => {
        return {
            propertyName,
            summary: accomodationSummary,
            description
        };
    }
);

const getReviewsScrollSelector = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.reviewId ? `[data-review="${reviewData.reviewId}"]` : '';
    }
);

const getReviewsModalScrollTop = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.scrollTop;
    }
);

const getOwnerSectionTitle = createSelector(
    [getTranslations, getHostType],
    (trans, hostType) => {
        return hostType.owner ? trans.owner : trans.propertyManager;
    }
);

const getSlpBreadcrumbLinks = createSelector(
    [getSearchLandingPages],
    (searchLandingPages) => {
        if (!searchLandingPages) {
            return {};
        }
        const {breadcrumbs = []} = searchLandingPages;
        const targetNum = 8;
        const linkArray = [];

        for (let crumb of breadcrumbs) {
            if (linkArray.length === targetNum) {
                break;
            }
            if (crumb.path && crumb.url) {
                linkArray.push({text: crumb.name, url: crumb.url});
            }
        }

        return {links: linkArray, isBreadcrumbs: true};
    }
);

/* eslint-disable complexity */
const getSlpRelatedLinks = createSelector(
    [getSearchLandingPages, getSlpBreadcrumbLinks],
    (searchLandingPages, slpBreadcrumbLinks) => {
        if (!searchLandingPages) {
            return {};
        }

        const {descendantsByType = {}, adjacentsByType = {}} = searchLandingPages;
        const allLinks = [
            ...(descendantsByType.pointOfInterest || []),
            ...(descendantsByType.neighborhood || []),
            ...(adjacentsByType.pointOfInterest || []),
            ...(adjacentsByType.neighborhood || [])
        ];
        const targetNum = 8 - slpBreadcrumbLinks.links.length;
        const linkArray = [];

        for (let link of allLinks) {
            if (linkArray.length === targetNum) {
                break;
            }
            if (link.path && link.url) {
                linkArray.push({text: link.simpleName, url: link.url});
            }
        }

        return {links: linkArray};
    }
);
/* eslint-enable complexity */

const getRecommendedPropertiesLinks = createSelector(
    [getRecommendedProperties],
    (recommendedProperties) => {
        const linkArray = [];
        if (!get(recommendedProperties, 'recommendedListings.length')) {
            return {};
        }

        for (let property of recommendedProperties.recommendedListings) {
            linkArray.push({
                text: get(property, 'listing.propertyMetadata.headline'),
                url: removeUniId(get(property, 'listing.detailPageUrl'))
            });
        }
        return {links: linkArray};
    }
);

const getSeoLinks = createSelector(
    [getSlpBreadcrumbLinks, getSlpRelatedLinks, getRecommendedPropertiesLinks],
    (slpBreadcrumbLinks, slpRelatedLinks, recommendedPropertiesLinks) => {
        const allLinksArray = [];

        for (let linkGroup of [slpBreadcrumbLinks, slpRelatedLinks, recommendedPropertiesLinks]) {
            if ((linkGroup.links || []).length) {
                allLinksArray.push(linkGroup);
            }
        }

        return allLinksArray;
    }
);

const getDisplayImprint = createSelector(
    [getTravelerFeatures],
    (travelerFeatures) => {
        return !!travelerFeatures.displayImprint;
    }
);

const getIsFamilySegment = createSelector(
    [getTripData],
    (tripData) => {
        return !!(tripData && tripData.isFamilySegment);
    }
);

const getShowMapSection = createSelector(
    [getMapData],
    (mapData) => {
        return !!(mapData.lat && mapData.lng);
    }
);

const getShowHouseRulesSection = createSelector(
    [getHouseRulesData],
    (houseRulesData) => {
        return !!(houseRulesData && houseRulesData.showHouseRulesSection);
    }
);

const getListingPageData = createSelector(
    [
        getAbacusTests,
        getAvailabilityCalendar,
        getBearStayBannerLabels,
        getBearStayReviewData,
        getBookingModalData,
        getBookingType,
        getBookingUrl,
        getBrandName,
        getBrowserName,
        getDates,
        getGuests,
        getSiteName,
        getIsFamilySegment,
        getIsTypeaheadInputFocused,
        getListingDescription,
        getBaseUrlPath,
        getListingPageModalStates,
        getListingSummary,
        getOwnerSectionTitle,
        getRateDetailsData,
        getRecommendedProperties,
        getReviewsModalScrollTop,
        getReviewsScrollSelector,
        getSeoLinks,
        getShowMapSection,
        getShowHouseRulesSection,
        getSystem,
        getTranslations,
        getTravelerFeatures,
        getTravelerSegmentFeatures,
        getDisplayImprint,
        getListingIsFamilyFriendly,
        getVirtualTourData,
        getFirstPayment,
        getViewDetailsTotalData,
        getMainImageHeight
    ],
    (
        abacusTestData,
        availabilityCalendar,
        bearStayBannerLabels,
        bearStayReviewData,
        bookingModalData,
        bookingType,
        bookingUrl,
        brandName,
        browserName,
        dates,
        guests,
        siteName,
        isFamilySegment,
        isTypeaheadInputFocused,
        listingDescription,
        baseUrlPath,
        listingPageModalStates,
        listingSummary,
        ownerSectionTitle,
        rateDetailsData,
        recommendedProperties,
        reviewsModalScrollTop,
        reviewsScrollSelector,
        seoLinks,
        showMapSection,
        showHouseRulesSection,
        system,
        translations,
        travelerFeatures,
        travelerSegmentFeatures,
        displayImprint,
        isFamilyFriendly,
        virtualTourData,
        firstPaymentData,
        viewDetailsTotalData,
        mainImageHeight
    ) => {
        const {showTopRecoCarousel, showMidpageRecoCarousel} = travelerSegmentFeatures;
        const {displayCivilCodeDisclaimerLink, isLoggedIn} = travelerFeatures;
        const {virtualTourId, virtualTourUrl, virtualTourHeroImage} = virtualTourData;

        return {
            abacusTestData,
            availabilityCalendar,
            bearStayBannerLabels,
            bearStayReviewData,
            bookingModalError: bookingModalData.currentError,
            bookingUrl,
            brandName,
            dates,
            guests,
            siteName,
            displayCivilCodeDisclaimerLink,
            isFamilySegment,
            isLoggedIn,
            isTypeaheadInputFocused,
            labels: translations,
            listingDescription,
            baseUrlPath,
            listingPageModalStates,
            listingSummary,
            ownerSectionTitle,
            propertyOlb: bookingType.onlineBookable,
            showRateDetails: rateDetailsData.showRateDetails,
            recommendedProperties,
            reviewsModalScrollTop,
            reviewsScrollSelector,
            seoLinks,
            showMapSection,
            showHouseRulesSection,
            showChat: browserName !== 'ie',
            showMidpageRecoCarousel,
            showTopRecoCarousel,
            system,
            displayImprint,
            isFamilyFriendly,
            virtualTourId,
            virtualTourUrl,
            virtualTourHeroImage,
            firstPaymentData,
            viewDetailsTotalData,
            navigationContainerHeight: mainImageHeight + 16,
            isTravelerLoggedIn: travelerFeatures.isLoggedIn
        };
    }
);

export default getListingPageData;
export {
    getListingDescription,
    getListingPageData,
    getOwnerSectionTitle,
    getReviewsModalScrollTop,
    getShowMapSection,
    getShowHouseRulesSection,
    getSlpBreadcrumbLinks,
    getSlpRelatedLinks,
    getRecommendedPropertiesLinks,
    getSeoLinks
};
